import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import Form from '@atlaskit/form';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { B300, N200, B400, N800 } from '@atlaskit/theme/colors';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  SanitizedFormattedHtmlMessage,
  useStargateClient,
  Storage
} from '@atlassiansox/bxpkit-core';

import {
  useAppDispatch,
  useAppSelector,
  updateSharedFormDataResendEmailAddress
} from '../../../../redux';
import {
  useSignUpFormGlobalConfiguration,
  useSignUpFormUIState
} from '../../../../hook';

import { messages } from '../../messages';
import { fontCharlieText, FormContainer, SubmitErrorMessage } from '../styled';
import { useSimpleSignupState } from '../../hook';
import {
  PageHeader,
  SocialSignUpDivider,
  InputEmail,
  ButtonSubmit,
  MarketingConsentCheckBox,
  SocialSignUpButton
} from '../component';
import { Base64 } from 'js-base64';

type FormProps = {
  readonly formProps: {
    readonly onSubmit: (e: React.FormEvent) => void;
    readonly ref: React.RefObject<HTMLFormElement>;
    readonly onKeyDown: (e: React.KeyboardEvent) => void;
  };
  readonly submitting: boolean;
  readonly change: any;
  readonly getValues: any;
  readonly getState: any;
};

function shouldSkipRecaptcha(email: string) {
  const botTestPattern =
    /(bottest@usersinbuckets.com|bottest@atl-paid-dev.com|bottest@bxpwpi.com)$/;
  return botTestPattern.test(email);
}

export function SimpleSignupFormLoggedOut() {
  const dispatch = useAppDispatch();
  const { checkEmailPageUrl: checkEmailPagePathname } =
    useSignUpFormGlobalConfiguration();
  const recaptchaRef = useRef<any | null>();
  const stargateClient = useStargateClient();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enableSkipRecaptcha, locale, intl } = useSimpleSignupState();
  const { signupState } = useSignUpFormUIState();
  const { consent } = signupState;
  consent.locale = locale.locale;

  // Feature flag placeholder
  const enableAllSocialSignupOptions = false;

  const showMarketingConsent = locale.localeRequiresMarketingCommunicationOptIn;

  const { email: emailValidation } = useAppSelector(
    ({ n2nDeferredPasswordFieldValidation }) =>
      n2nDeferredPasswordFieldValidation
  );
  const submitErrorMessage = useAppSelector(
    ({ sharedFormData: { submitErrorMessage } }) => submitErrorMessage
  );

  const redirectTo = window.location.href;

  useEffect(() => {
    recaptchaRef?.current?.reset();
  }, []);

  const onSubmitWithReCAPTCHA = useCallback(
    async (data: any) => {
      if (!(emailValidation === 'success')) {
        return;
      }
      setIsSubmitting(true);
      if (!executeRecaptcha) {
        console.log('Execute recaptcha not yet available');
        return;
      }
      const { email } = data;
      dispatch(updateSharedFormDataResendEmailAddress(email));
      const skipRecaptcha =
        data.email && enableSkipRecaptcha && shouldSkipRecaptcha(data.email);

      const token = skipRecaptcha
        ? 'reCaptcha'
        : await executeRecaptcha('userSignup');

      if (token != null) {
        const gRecaptchaResponse = token;

        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.submitWithCaptcha',
          eventComponent: 'button',
          event: 'clicked'
        }).fire();

        createAnalyticsEvent({
          eventName: 'bxp.formCloudSignup.formSubmit',
          eventComponent: 'button',
          event: 'clicked',
          data: {
            type: 'N2N'
          }
        }).fire();

        Storage.setLocalStorage('consentConfig', JSON.stringify(consent));
        await stargateClient
          .userSignup({
            email: email as string,
            locale: intl?.locale || 'en',
            redirectTo,
            displayName: '',
            gRecaptchaResponse
          })
          .then(() => {
            sessionStorage.setItem(
              'r.e.d',
              Base64.encode(JSON.stringify({ email, redirectTo }))
            );
          })
          .catch((error) => {
            createAnalyticsEvent({
              eventName: 'reactSignUpFormSubmitFailed',
              eventComponent: 'button',
              event: 'clicked',
              data: {
                event: 'submitFailed',
                uuid: 'reactSignUpForm'
              }
            }).fire();

            console.error(error);
            setIsSubmitting(false);
          });

        createAnalyticsEvent({
          eventName: 'reactSignUpFormSubmitSucceeded',
          eventComponent: 'button',
          event: 'clicked',
          data: {
            event: 'submitSucceeded',
            uuid: 'reactSignUpForm'
          }
        }).fire();
        window.location.assign(checkEmailPagePathname);
      } else {
        setIsSubmitting(false);
        throw new Error('Invalid Recaptcha Token');
      }
      return token;
    },
    [executeRecaptcha, emailValidation]
  );

  return (
    <>
      <PageHeader
        title={
          <FormattedMessage {...messages.simpleSignupLoggedOutFormHeader} />
        }
        subheadings={[
          <FormattedMessage {...messages.simpleSignupLoggedOutFormSubHeader} />,
          <FormattedMessage {...messages.noCreditCardRequiredFooter} />
        ]}
      />
      <FormContainer>
        <Form onSubmit={onSubmitWithReCAPTCHA} isDisabled={isSubmitting}>
          {({ formProps }: FormProps) => (
            <StyledForm {...formProps}>
              <InputEmail />
              {submitErrorMessage && (
                <SubmitErrorMessage>
                  <SanitizedFormattedHtmlMessage {...submitErrorMessage} />
                </SubmitErrorMessage>
              )}
              {showMarketingConsent && <MarketingConsentCheckBox />}
              <Eula>
                <SanitizedFormattedHtmlMessage {...messages.simpleSignupEula} />
              </Eula>
              <ButtonSubmit isLoading={isSubmitting} isDisabled={false} />
              <SocialSignUpDivider />
              <SocialSignUpButton
                buttonText={
                  <FormattedMessage
                    {...messages.socialSignUpButtonSignupWithGoogle}
                  />
                }
                provider="google"
                redirectUrl={redirectTo}
              />
              {enableAllSocialSignupOptions && (
                <>
                  <SocialSignUpButton
                    buttonText={
                      <FormattedMessage
                        {...messages.socialSignUpButtonSignupWithMicrosoft}
                      />
                    }
                    provider="microsoft"
                    redirectUrl={redirectTo}
                  />
                  <SocialSignUpButton
                    buttonText={
                      <FormattedMessage
                        {...messages.socialSignUpButtonSignupWithApple}
                      />
                    }
                    provider="apple"
                    redirectUrl={redirectTo}
                  />
                  <SocialSignUpButton
                    buttonText={
                      <FormattedMessage
                        {...messages.socialSignUpButtonSignupWithSlack}
                      />
                    }
                    provider="slack"
                    redirectUrl={redirectTo}
                  />
                </>
              )}
            </StyledForm>
          )}
        </Form>
      </FormContainer>
    </>
  );
}

const Eula = styled.div`
  font-family: ${fontCharlieText};
  font-size: 12px;
  line-height: 1.5;
  color: ${N800};
  font-weight: 400;
  margin: 24px 0;

  a {
    color: ${B400};
    cursor: pointer;
    text-decoration: none;

    &:hover {
      color: ${B300};
      text-decoration: underline;
    }
  }
`;

const StyledForm = styled.form`
  width: 360px;
  font-family: ${fontCharlieText};

  label {
    color: ${N200};
    font-weight: 500;

    span {
      display: none;
    }
  }
`;
